import { SafePromise, safeResilient } from '@benzinga/safe-await';
import { Session, RestfulClient, DataRequestInit, proxyClientOptions } from '@benzinga/session';
import { CommonCalendarEnvironmentProps, CommonCalendarRoute } from './environment';
import { genUrlParams, CalendarApiParams } from './urlProps';
import { CommonCalendarQueryParams } from './entities';

export interface CalendarRestfulOptions<P extends CommonCalendarQueryParams = CommonCalendarQueryParams> {
  credentials?: DataRequestInit['credentials'];
  genUrlParams?: (params: P) => CalendarApiParams;
}

export class CommonCalendarRestful extends RestfulClient {
  private route?: CommonCalendarRoute;
  private tokenOverride: string;

  constructor(session: Session, env: CommonCalendarEnvironmentProps) {
    super(env.url, session, {}, env.route ? proxyClientOptions(undefined, 'token') : { tokenParameterName: 'token' });
    this.route = env.route;
    this.tokenOverride = env.token;
  }

  public fetchCalendarData = <T, P extends CommonCalendarQueryParams = CommonCalendarQueryParams>(
    params: P,
    options: CalendarRestfulOptions<P> = {},
  ): SafePromise<T> => {
    const urlParams: CalendarApiParams = options.genUrlParams?.(params) ?? genUrlParams(params);

    if (!this.usingProxy && this.tokenOverride) {
      urlParams['token'] = this.tokenOverride;
    }

    const path = this.route ? this.routePath(this.route) : undefined;
    const url = this.URL(path, urlParams);

    const resilientGet = safeResilient((input: URL, init: Partial<DataRequestInit> = {}) => this.get<T>(input, init), {
      delayOffset: 30000,
      maxNumOfAttempts: 10,
      retryOnError: true,
      returnLastError: true,
    });

    return resilientGet(url, {
      allowsAnonymousAuth: true,
      credentials: options.credentials ?? (this.usingProxy ? 'same-origin' : undefined),
    }).then(response => {
      if (response.err) {
        return { err: response.err };
      }
      return { ok: response.ok };
    });
  };
}
